import { memo } from 'react';
import { Handle, Position, NodeResizer, type NodeProps } from '@xyflow/react';
import { Repeat, Trash2, Zap, CheckCircle2, XCircle, Loader2, RotateCcw } from 'lucide-react';
import { LOOP_MODE_LABELS } from '../../lib/catalog';
import { nodeColors } from '../../theme/colors';
import { cn } from '../../lib/utils';
import { useFlowStore, type StudioNodeData } from '../../store/flowStore';

const handleClass = '!h-2.5 !w-2.5 !border-2 !border-white !bg-node-loop';

// Nó Loop renderizado como CONTAINER: header próprio + corpo âmbar que envolve os nós-filhos.
function LoopGroupImpl({ id, data, selected }: NodeProps) {
  const nodeData = data as StudioNodeData;
  const c = nodeData.config;
  const requestDeleteNodes = useFlowStore((s) => s.requestDeleteNodes);
  const setLoopSize = useFlowStore((s) => s.setLoopSize);
  const runState = useFlowStore((s) => s.runStatus[id]);

  const parallel = Boolean(c.parallel);
  const maxIter = Number(c.maxIterations) || 0;
  const label = (c.label as string) || '';
  const mode = LOOP_MODE_LABELS[String(c.mode)] ?? 'Loop';

  const running = runState?.status === 'running';
  const done = runState?.status === 'done';
  const errored = runState?.status === 'error';

  return (
    <div
      className={cn(
        'flex h-full w-full flex-col rounded-2xl border-2 border-dashed bg-amber/[0.07] transition-colors',
        selected ? 'border-node-loop ring-2 ring-node-loop/30' : 'border-amber/60',
        running && 'border-node-loop',
        done && 'border-success/70',
        errored && 'border-danger/70',
      )}
      style={{ borderColor: selected ? nodeColors.loop : undefined }}
    >
      {/* Redimensionamento livre (largura e altura) — alças aparecem ao selecionar */}
      <NodeResizer
        color={nodeColors.loop}
        isVisible={selected}
        minWidth={260}
        minHeight={150}
        onResize={(_, params) => setLoopSize(id, params.width, params.height)}
      />

      <Handle type="target" position={Position.Left} className={handleClass} />
      <Handle type="source" position={Position.Right} className={handleClass} />
      <Handle type="target" position={Position.Top} id="t" className={handleClass} />
      <Handle type="source" position={Position.Bottom} id="b" className={handleClass} />

      {/* Header do container */}
      <div className="flex items-center justify-between rounded-t-2xl border-b border-amber/40 bg-amber/20 px-3 py-2">
        <div className="flex items-center gap-2">
          <div
            className="flex h-7 w-7 items-center justify-center rounded-lg text-white"
            style={{ backgroundColor: nodeColors.loop }}
          >
            <Repeat className="h-4 w-4" />
          </div>
          <div className="leading-tight">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate">Controle</p>
            <p className="text-sm font-semibold text-ink">
              Loop{label ? ` de ${label}` : ''}
              {parallel ? ' (Paralelo)' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {parallel && (
            <span className="inline-flex items-center gap-1 rounded-full bg-node-loop/15 px-2 py-0.5 text-[10px] font-semibold text-node-loop">
              <Zap className="h-3 w-3" /> paralelo
            </span>
          )}
          <span className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-0.5 text-[11px] font-semibold text-ink shadow-sm">
            {running && <Loader2 className="h-3 w-3 animate-spin text-node-loop" />}
            {done && <CheckCircle2 className="h-3 w-3 text-success" />}
            {errored && <XCircle className="h-3 w-3 text-danger" />}
            {runState?.iteration ? `iteração ${runState.iteration}/${maxIter}` : `${mode} · máx ${maxIter}`}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              requestDeleteNodes([id]);
            }}
            className="rounded-md p-1 text-slate hover:bg-white hover:text-danger"
            aria-label="Excluir loop"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Corpo: os nós-filhos são renderizados pelo React Flow por cima desta área.
          O indicador de repetição abaixo é DECORATIVO (não é uma aresta), então nunca embola. */}
      <div className="pointer-events-none relative flex-1">
        <div className="absolute inset-x-4 bottom-2 flex items-center gap-2">
          <div className="h-0 flex-1 border-t-2 border-dashed border-node-loop/45" />
          <span className="inline-flex items-center gap-1 rounded-full border border-node-loop/30 bg-white px-2 py-0.5 text-[10px] font-semibold text-node-loop shadow-sm">
            <RotateCcw className="h-3 w-3" />
            repete
            {c.stopCondition ? ` até: ${String(c.stopCondition)}` : ` · máx ${maxIter}`}
          </span>
          <div className="h-0 flex-1 border-t-2 border-dashed border-node-loop/45" />
        </div>
      </div>
    </div>
  );
}

export const LoopGroupNode = memo(LoopGroupImpl);
